<template>
    <div class="page-modal">
        <widgetModal title="Edit Contact Type" :subtitle="contact ? `${contact.first_name} ${contact.last_name}` : ''"
            btnlefttext="Save Changes" btncentertext="Cancel" :leftloading="isSubmitting" :centerloading="false"
            :footerhidden="false" :hiderightbtn="true" footer_padding="10px 18px 30px 30px"
            header_padding="30px 0px 0px 31px" body_padding="0px 30px" width="500px" @onLeftClick="saveChanges"
            @onCenterClick="onCancel">
            <div class="widget-modal-body-noscoll">
                <div class="user-info" v-if="contact">
                    <div class="user-avatar">
                        <img src="@/assets/contact-avatar.svg" alt="Contact Avatar">
                    </div>
                    <div class="user-details">
                        <h4>{{ contact.first_name }} {{ contact.last_name }}</h4>
                        <p>{{ contact.email }}</p>
                        <p v-if="contact.position">{{ contact.position }}</p>
                    </div>
                </div>

                <div class="roles-section">
                    <h4>Current Contact Types</h4>
                    <div class="current-roles" v-if="contact && contact.roles && contact.roles.length > 0">
                        <div class="role-item" v-for="(role, index) in editableRoles" :key="index">
                            <input v-model="role.name" @input="onRoleEdit(role, index)" class="role-input" type="text"
                                :placeholder="'Contact Type ' + (index + 1)" />
                            <button @click="removeRole(index)" class="remove-btn" type="button">
                                <img src="@/assets/admin-icon/cross-icon.svg" alt="Remove">
                            </button>
                        </div>
                    </div>
                    <div v-else class="no-roles">
                        <p>No contact types found for this contact</p>
                    </div>

                    <div class="add-new-role">
                        <h4>Add New Contact Types</h4>
                        <widgetTags v-model="newRoles" input_width="100%" placeholder="Add new contact types">
                        </widgetTags>
                    </div>
                </div>
            </div>
        </widgetModal>
    </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: "edit-contact-roles-modal",
    props: {
        contact: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            editableRoles: [],
            newRoles: {
                added: [],
                removed: []
            },
            isSubmitting: false,
        };
    },
    mounted() {
        this.initializeRoles();
    },
    methods: {
        ...mapActions(['CONTACT_UPDATE_ROLES']),

        initializeRoles() {
            // Create a copy of existing contact types for editing
            if (this.contact && this.contact.roles) {
                this.editableRoles = this.contact.roles.map(role => ({
                    id: role.id,
                    name: role.name,
                    originalName: role.name,
                    isModified: false
                }));
            }
        },

        onRoleEdit(role, index) {
            // Mark contact type as modified if the name changed
            role.isModified = role.name !== role.originalName;
        },

        removeRole(index) {
            const removedRole = this.editableRoles.splice(index, 1)[0];
            // If it was an existing contact type, track it for removal
            if (removedRole.id) {
                this.newRoles.removed.push(removedRole.id);
            }
        },

        async saveChanges() {
            this.isSubmitting = true;

            try {
                // Prepare the payload
                const payload = {
                    contact_id: this.contact.id,
                    updated_roles: this.editableRoles.filter(role => role.isModified && role.name.trim() !== ''),
                    new_roles: this.newRoles.added,
                    removed_roles: this.newRoles.removed
                };

                // Call the API
                const response = await this.CONTACT_UPDATE_ROLES(payload);

                this.$notify({
                    group: "status",
                    title: "Success",
                    text: "Contact types updated successfully",
                    type: "success",
                    duration: 3000,
                });

                // Emit success event with updated data
                this.$emit('onRolesUpdated', response.data);
                this.$emit('onCancel'); // Close modal

            } catch (error) {
                console.error('Error updating contact types:', error);
                this.$notify({
                    group: "status",
                    title: "Error",
                    text: "Failed to update contact types",
                    type: "warn",
                    duration: 3000,
                });
            } finally {
                this.isSubmitting = false;
            }
        },

        onCancel() {
            this.$emit('onCancel');
        },
    },
};
</script>

<style lang="scss" scoped>
$assets: "~@/assets";

.page-modal {
    .user-info {
        display: flex;
        align-items: center;
        margin-bottom: 30px;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 10px;

        .user-avatar {
            margin-right: 15px;

            img {
                width: 60px;
                height: 60px;
                border-radius: 50%;
                object-fit: cover;
            }
        }

        .user-details {
            h4 {
                margin: 0 0 5px 0;
                font-size: 18px;
                color: #333;
            }

            p {
                margin: 0 0 3px 0;
                font-size: 14px;
                color: #666;
            }
        }
    }

    .roles-section {
        h4 {
            margin: 0 0 15px 0;
            font-size: 16px;
            color: #333;
        }

        .current-roles {
            margin-bottom: 30px;

            .role-item {
                display: flex;
                align-items: center;
                margin-bottom: 10px;
                border: 1px solid #ddd;
                border-radius: 8px;
                padding: 5px;
                background: #fff;

                .role-input {
                    flex: 1;
                    border: none;
                    outline: none;
                    padding: 8px 12px;
                    font-size: 14px;
                    border-radius: 6px;

                    &:focus {
                        background: #f0f8ff;
                    }
                }

                .remove-btn {
                    background: #ff4757;
                    border: none;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    margin-left: 8px;

                    &:hover {
                        background: #ff3838;
                    }

                    img {
                        width: 12px;
                        height: 12px;
                        filter: brightness(0) invert(1);
                    }
                }
            }
        }

        .no-roles {
            text-align: center;
            padding: 20px;
            color: #999;
            font-style: italic;
            margin-bottom: 30px;
        }

        .add-new-role {
            h4 {
                margin-bottom: 10px;
                color: #1BA5A4;
            }
        }
    }
}
</style>